import { CSSProperties } from "react";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  rydeColors,
} from "@ryde/theme";

import {
  SWATCH_DOT_SIZE_PX,
  SWATCH_INACTIVE_OPACITY,
  SWATCH_LINE_BORDER_PX,
  SWATCH_LINE_WIDTH_PX,
} from "./investigation.constants";

/** Full-body investigation workspace: map column | draggable resizer | info panel column. */
export const WorkspaceStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-workspace",
})`
  display: flex;
  width: 100%;
  /* Fills the overlay host outright — the workspace no longer measures the viewport itself. */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${rydeColors.white};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
  }
`;

export const MapColumnStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-map-column",
})`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  position: relative;
`;

export const MapAreaStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-map-area",
})`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
`;

export const ResizerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-resizer",
})`
  flex: 0 0 6px;
  cursor: col-resize;
  background: ${greyMui._200};
  touch-action: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${blueMui._300};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

export const PanelColumnStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-column",
})`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  min-width: 0;
  background: ${rydeColors.white};
  border-left: 1px solid ${greyMui._200};
  overflow: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 1 1 auto;
    width: 100% !important;
    border-left: none;
    border-top: 1px solid ${greyMui._200};
  }
`;

export const InvestigationTopBarStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-top-bar",
})`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  background: ${rydeColors.white};
  border-bottom: 1px solid ${greyMui._200};
`;

export const InvestigationTopBarTitleStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-investigation-top-bar-title",
  },
)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${blueGreyMui._900};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: ${blueMui._600};
    flex: 0 0 auto;
  }
`;

export const ToggleGroupStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-toggle-group",
})`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ToggleChipStyled = styled.button.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-filter-toggle",
})<{ $active: boolean; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid
    ${({ $active, $color }) => ($active ? $color : greyMui._300)};
  background: ${({ $active }) => ($active ? greyMui._100 : rydeColors.white)};
  color: ${({ $active, $color }) => ($active ? $color : greyMui._600)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/** Round dot swatch for a filter chip (e.g. the trail-sample dots layer). */
export const FilterSwatchDotStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-filter-swatch-dot",
})<{
  $color: string;
  $active: boolean;
}>`
  width: ${SWATCH_DOT_SIZE_PX}px;
  height: ${SWATCH_DOT_SIZE_PX}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  opacity: ${({ $active }) => ($active ? 1 : SWATCH_INACTIVE_OPACITY)};
`;

/** Line swatch for a filter chip; dash style reflects the layer (solid / dashed / dotted). */
export const FilterSwatchLineStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-filter-swatch-line",
})<{
  $color: string;
  $active: boolean;
  $dash: CSSProperties["borderTopStyle"];
}>`
  width: ${SWATCH_LINE_WIDTH_PX}px;
  border-top-width: ${SWATCH_LINE_BORDER_PX}px;
  border-top-style: ${({ $dash }) => $dash};
  border-top-color: ${({ $color }) => $color};
  opacity: ${({ $active }) => ($active ? 1 : SWATCH_INACTIVE_OPACITY)};
`;

export const PanelHeaderStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-header",
})`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid ${greyMui._200};
`;

export const PanelHeaderTitleStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-header-title",
})`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: ${blueGreyMui._900};

  svg {
    color: ${blueMui._600};
  }
`;

export const PanelScrollStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-scroll",
})`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px;
`;
